import * as React from "react";
import { observer, inject } from "mobx-react";
import Editor from "../../editor/Editor";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import {
  Typography,
  CircularProgress,
  Avatar,
  Input,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import autobind from "autobind-decorator";
import AccountService from "../../service/AccountService";
import { RouterStore } from "mobx-react-router";
import { observable } from "mobx";
import FileService, { NarrativeMetadata } from "../../service/FileService";
import { deserializeLayerGroup } from "../../editor/serializer";
import { QuestionnaireChatState } from "../../editor-ui/QuestionnaireChatRenderer";
import NarrativeBuilder from "../../editor/NarrativeBuilder";
import { getPlatformType } from "../../Config";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import { questionTypes } from "../../editor/NodeLayer";
import firebase from "firebase/compat/app";
import QuestionnaireEmbeddedRenderer, {
  RenderAnswerSelector,
} from "../../editor-ui/QuestionnaireEmbeddedRenderer";
import { v4 as uuid } from "uuid";
import CustomLinkify from "./components/CustomLinkify";
import { Close } from "@mui/icons-material";

declare var Keyboard: any;

const ModalButton = styled("button")({
  width: "64px",
  lineHeight: "28px",
  height: "28px",
  fontSize: "12px",
  background: "rgb(155, 66, 227)",
  color: "white",
  cursor: "pointer",
  borderRadius: "4px",
  outline: "none",
  border: "none",
  transition: "all 100ms ease-in-out",
  textAlign: "center",
});
const CancelButton = styled(ModalButton)({
  backgroundColor: "rgb(211, 69, 91)",
  marginRight: "10px",
});

const NextButton = styled(ModalButton)({
  backgroundColor: "#f50057",
  fontWeight: "bold",
  marginLeft: "2rem",
  width: "80px",
});
const PreviousButton = styled(ModalButton)({
  backgroundColor: "#f50057",
  fontWeight: "bold",
  marginRight: "2rem",
  width: "80px",
});

const ButtonRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "18px",
});

const CustomButton = styled("button")({
  backgroundColor: "#fff",
  color: "#3498ff",
  border: "1px solid #3498ff",
  borderRadius: "4px",
  transition: "all 0.3s ease-in-out",
  padding: "0.4rem 1rem",
  margin: "0 0.8rem",
  width: "100px",
  boxShadow: "1px 2px 5px lightgray",

  "&:hover": {
    backgroundColor: "#3498ff",
    color: "#fff",
    border: "1px solid #3498ff",
  },
});

const DisabledCustomButton = styled("button")({
  backgroundColor: "lightgray",
  color: "#fff",
  cursor: "not-allowed",
  border: "1px solid #lightgray",
  borderRadius: "4px",
  padding: "0.4rem 1rem",
  margin: "0 0.8rem",
  width: "100px",
});

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "fit-content",
  alignItems: "center",
});

const LoaderContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  alignItems: "center",
  justifyContent: "center",
});

const TextInput = styled("input")({
  outline: "none",
  border: "1px solid rgb(220, 220, 220)",
  fontSize: "12px",
  padding: "8px",
  color: "rgb(80, 80, 80)",
  resize: "none",
  margin: "12px",
  width: "320px",

  "&:focus": {
    border: "1px solid rgb(180, 180, 180)",
  },
});

const QuestionText = styled("p")({
  margin: "12px",
  fontSize: "32px",
  minHeight: "32px",
  color: "rgb(80, 80, 80)",
});

const ChatHead = styled("div")({
  height: "60px",
  display: "flex",
  flexDirection: "row",
  backgroundColor: "#F50057",
  padding: "10px 20px",
});

const ChatBody = styled("div")({
  marginBottom: "60px",
});

const ChatFoot = styled("div")({
  width: "100%",
  height: "60px",
  backgroundColor: "#232832",
  color: "#5B5D65",
  alignContent: "center",
  justifyContent: "center",
  display: "flex",
  flexDirection: "row",
  padding: "0px 10px",
  position: "absolute",
  bottom: 0,
});

const Span = styled("div")({
  display: "flex",
  flexGrow: 1,
});

type messageType = {
  question: string;
  answer: string;
};

type EditorPageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
  match?: any;
};

type EditorPageStatus =
  | "LOADING"
  | "NORMAL"
  | "GET_NAME"
  | "NO_START_NODE"
  | "COMPLETE";

@inject("account")
@inject("routing")
@inject("files")
@inject("send")
@observer
export default class QuestionnaireEmbeddedPage extends React.Component<
  EditorPageProps,
  { progress: number; disabledNextButton: boolean; responseLength: number }
> {
  private _editor = new Editor();
  private _questionnaireState = new QuestionnaireChatState(this._editor);
  state = {
    progress: 10,
    disabledNextButton: false,
    responseLength: 0,
  };

  @observable
  private _status: EditorPageStatus = "LOADING";

  @observable
  private _fileName = "";

  @observable
  private _name = "";

  @observable
  private _qAnswer = "";

  @observable
  private _insetHeight = 0;

  @observable
  private _insetRef: HTMLElement;

  @observable
  messageArray: Array<messageType> = [];

  @observable
  endDialogOpen: boolean = true;

  private _userData = {} as any;

  @autobind
  goBack() {
    var { account } = this.props;
    account.goBack();
  }

  @autobind
  handleKeyPress(e) {
    if (e.keyCode == 13) {
      this.handleNext();
    }

    e.stopPropagation();
  }

  @autobind
  handlePrevious() {
    if (this.messageArray.length >= 1) {
      this.messageArray = this.messageArray.slice(
        0,
        this.messageArray.length - 1
      );
    } else {
      if (this._name !== "Anonymous Guest" || "" || null) {
        this.messageArray = [];
        this._name = "";
        this._qAnswer = "";
        this._status = "GET_NAME";
      } else {
        return;
      }
    }
    this._questionnaireState.handlePrevious();
  }

  @autobind
  async handleNext() {
    if (this._status == "GET_NAME") {
      this._name = this._qAnswer;
      this._qAnswer = "";
      this._status = "NORMAL";
      return;
    }

    if (this._status == "NORMAL") {
      this._qAnswer &&
        this._questionnaireState.currentQuestionNode.setAnswer(this._qAnswer);
      let tempMessage = {
        question: this._questionnaireState.currentQuestionNode.questionText,
        answer: this._questionnaireState.currentQuestionNode.answers
          .filter((ans) => ans)
          .join(","),
      };
      this.messageArray.push(tempMessage);

      this._questionnaireState.handleNext();
      this._qAnswer = "";
      // if (this._questionnaireState.isComplete) {
      //   this._status = 'LOADING';
      //   var builder = new NarrativeBuilder(this._editor);
      //   var { narrative, responses } = builder.buildNarrative();

      //   const funcs = firebase.functions();
      //   const saveFunc = funcs.httpsCallable('cloudSaveNarrative');

      //   let result = await saveFunc({
      //     data: {
      //       flowId: id,
      //       flowName: this._fileName,
      //       date: new Date().toString(),
      //       userName: this._name,
      //       narrative: narrative,
      //       responses: responses,
      //       clinic: clinicId,
      //     },
      //     userId: userId,
      //   });

      //   this._status = 'COMPLETE';
      // }
      if (this._questionnaireState.isComplete) {
        this._status = "LOADING";
        await this.updateNarrativeData();
        this._status = "COMPLETE";
      }
    }
  }

  @autobind
  async updateNarrativeData() {
    const { clinicId, id, userId } = this.props.match.params;
    var builder = new NarrativeBuilder(this._editor);
    var { narrative, responses } = builder.buildNarrative();
    const updateResponses = responses.slice(0, this.messageArray.length);
    const narrativeData: NarrativeMetadata = {
      id: this.props.files.currentNarrativeId,
      flowId: id,
      flowName: this._fileName,
      date: new Date().toString(),
      userName: this._name,
      narrative: narrative,
      responses: updateResponses,
      clinic: clinicId,
    };
    const functions = firebase.functions();
    const updateNarrative = functions.httpsCallable("cloudUpdateNarrative");
    await updateNarrative({
      data: narrativeData,
      userId,
      currentNarrativeId: this.props.files.currentNarrativeId,
    });
  }

  @autobind
  handleEditFlow() {
    var { routing } = this.props;
    const { id } = this.props.match.params;
    routing.replace(`/editor/${id}`);
  }

  @autobind
  reload() {
    var { routing } = this.props;
    const { id, userId, clinicId } = this.props.match.params;
    routing.replace("/blank");
    let link = `/questionnaire/${userId}/${clinicId}/${id}`;

    setTimeout(() => routing.replace(link));
  }

  @autobind
  redirect() {
    var { routing } = this.props;
    const url = this._editor.startLayer?.redirectUrl;
    if (url) {
      if (url.includes("http")) window.location.href = url;
      else window.location.href = `http://${url}`;
    } else {
      routing.replace("/");
    }
  }

  @autobind
  checkDisabled(): boolean {
    if (this._questionnaireState.currentQuestionNode == null) {
      return true;
    }
    if (
      this._questionnaireState.currentQuestionNode.questionType ==
        questionTypes.singleChoice ||
      this._questionnaireState.currentQuestionNode.questionType ==
        "Single Choice"
    ) {
      if (this._questionnaireState.currentQuestionNode.answer === "")
        return true;
    }
  }

  async componentDidMount() {
    const { id, userId } = this.props.match.params;
    const { routing, files, account } = this.props;
    // new code
    const narrativeId = uuid();
    files.setCurrentNarrativeId(narrativeId);
    localStorage.setItem("Qid", id);

    if (id == null || userId == null) {
      routing.replace("/");
      return;
    }

    this._status = "LOADING";

    this._userData = (await account.fetchUserData(userId)) || {};
    var fileData = await files.loadFileDataUnsecure(id, userId);
    var fileName = await files.loadFileNameUnsecure(id, userId);

    if (fileData == "UNKNOWN_ERROR" || fileName == "UNKNOWN_ERROR") {
      routing.replace("/");
      return;
    }

    this._fileName = fileName;
    this._editor = new Editor();

    if (fileData != "") {
      deserializeLayerGroup(fileData, this._editor);
    }

    this._questionnaireState = new QuestionnaireChatState(this._editor);

    if (this._editor.startLayer == null) {
      this._status = "NO_START_NODE";
      return;
    }

    this._status = "GET_NAME";

    if (this._status == "GET_NAME" && !this._editor.startLayer.enabledName) {
      this._name = "Anonymous Guest";
      this._qAnswer = "";
      this._status = "NORMAL";
    }

    if (this._insetRef != null) {
      this._insetHeight = this._insetRef.offsetHeight;
    }
  }

  async componentDidUpdate(
    prevProps: Readonly<EditorPageProps>,
    prevState: Readonly<{ progress: number; responseLength: number }>
  ): Promise<any> {
    const { questionType } =
      this._questionnaireState?.currentQuestionNode ?? {};
    if (
      this._status == "NORMAL" &&
      prevState.responseLength !== this.messageArray.length
    ) {
      this.setState({
        ...this.state,
        responseLength: this.messageArray.length,
      });
      await this.updateNarrativeData();
    }
    //  else if (
    //   this._status == "COMPLETE"
    // ) {
    //   setTimeout(() => {
    //     this.redirect()
    //   }, 5000);
    // }

    if (questionType && questionType === "Without Response") {
      // update current question
      this._qAnswer = "";
      // move to the next question
      this._questionnaireState?.currentQuestionNode && this.handleNext();
    }
  }

  renderBotMessageComponent(message: string, replyOptions?) {
    return (
      <Box
        width="100%"
        padding="10px 20px"
        display="flex"
        flexDirection="row"
        alignItems="center"
      >
        <Box
          // backgroundColor: '#E9EEF3',
          // borderRadius: '40px',
          color="#5B5D65"
          fontSize="1.1rem"
          textAlign="center"
          alignContent="center"
          padding="15px"
          margin="0px 10px"
        >
          <CustomLinkify str={message} />
        </Box>
      </Box>
    );
  }

  linearProgressWithLabel(progress: number) {
    return (
      <Box display="flex" alignItems="center">
        <Box width="100%" mr={1}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
        <Box minWidth={35}>
          <Typography variant="body2" color="textSecondary">{`${Math.round(
            progress
          )}%`}</Typography>
        </Box>
      </Box>
    );
  }

  renderInputMessageComponent(message: string, replyOptions?) {
    return (
      <>
        {message && (
          <div
            style={{
              width: "100%",
              padding: "10px 20px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {/* <div style={{textAlign: 'center', verticalAlign: 'center',color: '#E9EEF3'}}>{Date().toString()}</div> */}
            <div
              style={{
                backgroundColor: "#1C8EFF",
                borderRadius: "40px",
                color: "#F9FCFF",
                display: "flex",
                textAlign: "left",
                alignContent: "center",
                padding: "10px",
                margin: "0px 10px",
              }}
            >
              {message}
            </div>
            <Avatar style={{ backgroundColor: "#1A1A1A" }}>
              <PermIdentityIcon />
            </Avatar>
          </div>
        )}
      </>
    );
  }

  renderQuestionnaireComponent(question: string, answer: string, i: number) {
    return <div key={i}>{this.renderBotMessageComponent(question)}</div>;
  }

  renderLoading() {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  renderQuestionnaire() {
    return (
      <React.Fragment>
        <QuestionnaireEmbeddedRenderer
          editor={this._editor}
          profilePic={this._userData.displayPic}
          state={this._questionnaireState}
          optioOnClick={this.handleNext}
        />
      </React.Fragment>
    );
  }

  renderLastQuestionnaire() {
    return (
      <React.Fragment>
        <Box
          width="100%"
          padding="10px 20px"
          display="flex"
          flexDirection="row"
        >
          <Box color="#5B5D65" alignContent="center">
            <CustomLinkify
              str={this.messageArray[this.messageArray.length - 1].question}
            />
          </Box>
        </Box>
      </React.Fragment>
    );
  }

  renderGetName() {
    return this.renderBotMessageComponent("Hi! Please tell me your name.");
  }

  renderButtonPanel() {
    return (
      <ButtonRow>
        <NextButton onClick={this.handleNext}> NEXT </NextButton>
      </ButtonRow>
    );
  }

  renderNoStartNode() {
    return (
      <div
        style={{
          marginLeft: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <QuestionText>
          You must first go to https://flowcharts.ai to create your flow chart,
          workflow, dynamic questionnaire or survey. Once you do that, you can
          then use this mobile companion to administer it from this app in order
          to collect results from your respondents
        </QuestionText>

        {getPlatformType() == "BROWSER" ? (
          <ButtonRow>
            <NextButton onClick={this.handleEditFlow}> EDIT FLOW </NextButton>
          </ButtonRow>
        ) : null}
      </div>
    );
  }

  closeHandler() {
    window.close();
    window.parent.postMessage({ complete: true }, "*");
    setTimeout(() => {
      this.redirect();
    }, 1000);
  }

  checkCases() {
    let state = false;
    const question = this._questionnaireState.currentQuestionNode;
    if (this._status === "GET_NAME") {
      if (this._qAnswer !== "") state = true;
    } else {
      if (question?.questionType === questionTypes.text && this._qAnswer !== "")
        state = true;
      if (
        question?.questionType === questionTypes.yesNo ||
        question?.questionType === questionTypes.singleChoice ||
        question?.questionType === "Single Choice"
      ) {
        if (question?.answer !== "" && question?.answer !== null) {
          state = true;
        }
      }
      if (
        (question?.questionType === questionTypes.multiChoice ||
          question?.questionType === "Multiple Choice") &&
        question?.answers.length > 0
      )
        state = true;
    }

    return state;
  }

  render() {
    return (
      <Root style={{ position: "relative", backgroundColor: "#fff" }}>
        <div
          style={{
            width: "100%",
            height: "env(safe-area-inset-top)",
            backgroundColor: "inherit",
          }}
          ref={(it) => (this._insetRef = it)}
        />
        {/* {this.renderNavBar()} */}
        <div
          id="scrollDiv"
          style={{
            position: "relative",
            width: "100%",
            overflowY: "scroll",
            scrollbarWidth: "none",
            paddingTop: "24px",
            msOverflowStyle: "none",
          }}
        >
          <ChatBody>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "2rem",
                backgroundColor: "#fff",
              }}
            >
              <h2 style={{ fontSize: "1.1rem", marginBottom: "1rem" }}>
                {this._status == "NO_START_NODE"
                  ? this.renderNoStartNode()
                  : null}
                {this._status == "LOADING" ? this.renderLoading() : null}
                {this._status !== "NO_START_NODE" &&
                this._status !== "LOADING" &&
                this._status !== "NORMAL" &&
                this._status !== "COMPLETE" &&
                this._editor.startLayer.enabledName
                  ? this.renderGetName()
                  : null}

                {this._status == "NORMAL" ? this.renderQuestionnaire() : null}
                {this._status == "COMPLETE" &&
                !this._editor.startLayer.finalQuestionStatus
                  ? "Thank you for your responses. Your responses are submitted"
                  : null}
                {this._status == "COMPLETE" &&
                this._editor.startLayer.finalQuestionStatus ? (
                  <>
                    {this.renderLastQuestionnaire()}
                    <Box
                      width="100%"
                      display="flex"
                      justifyContent="center"
                      p="12px"
                      fontSize="1.5rem"
                    >
                      <Button
                        color="primary"
                        sx={{
                          width: "200px",
                          borderRadius: "30px",
                          padding: "1rem 6px",
                        }}
                        variant="contained"
                        onClick={this.closeHandler}
                      >
                        <Close sx={{ marginRight: "0.3rem" }} />
                        close
                      </Button>
                    </Box>
                  </>
                ) : null}
              </h2>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                width="100%"
              >
                <div style={{ minWidth: "400px" }}>
                  {(this._status === "GET_NAME" ||
                    (this._status === "NORMAL" &&
                      this._questionnaireState.currentQuestionNode
                        ?.questionType === questionTypes.text)) && (
                    <Input
                      value={this._qAnswer}
                      onChange={(ev) => (this._qAnswer = ev.target.value)}
                      // onKeyDown={this.handleKeyPress}
                      disableUnderline={true}
                      placeholder="Type your message here"
                      style={{
                        display: "flex",
                        flexGrow: 1,
                        alignSelf: "center",
                        border: "1px solid #ccc",
                        padding: "0.5rem 1rem",
                        borderRadius: "6px",
                      }}
                    />
                  )}
                  {this._status === "NORMAL" &&
                    this._questionnaireState.currentQuestionNode
                      ?.questionType !== questionTypes.text && (
                      <RenderAnswerSelector
                        state={this._questionnaireState}
                        editor={this._editor}
                        optioOnClick={this.handleNext}
                      />
                    )}
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  {this._status !== "COMPLETE" &&
                    this._status !== "LOADING" && (
                      <ButtonRow>
                        {this._status !== "GET_NAME" &&
                          this.messageArray.length > 0 && (
                            <CustomButton onClick={this.handlePrevious}>
                              Previous
                            </CustomButton>
                          )}
                      </ButtonRow>
                    )}
                  {this._status !== "COMPLETE" &&
                    this._status !== "LOADING" && (
                      <ButtonRow>
                        {this.checkCases() ? (
                          <CustomButton onClick={this.handleNext}>
                            Next
                          </CustomButton>
                        ) : (
                          <DisabledCustomButton>Next</DisabledCustomButton>
                        )}
                      </ButtonRow>
                    )}
                </div>
              </Box>
            </div>
          </ChatBody>
        </div>
      </Root>
    );
  }
}
